/** Seeded PRNG (mulberry32) with serialize/restore. */

export interface RngState {
  seed: number;
}

export interface Rng {
  state: RngState;
  next(): number;
  nextInt(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  fork(): Rng;
}

function mulberry32(a: number): () => number {
  let state = a >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createRng(seed: number): Rng {
  const state: RngState = { seed: seed >>> 0 };
  const nextFn = mulberry32(state.seed);

  const api: Rng = {
    state,
    next(): number {
      const v = nextFn();
      state.seed = (state.seed + 0x6d2b79f5) >>> 0;
      return v;
    },
    nextInt(min: number, max: number): number {
      const lo = Math.ceil(min);
      const hi = Math.floor(max);
      if (hi <= lo) return lo;
      return lo + Math.floor(api.next() * (hi - lo + 1));
    },
    pick<T>(items: readonly T[]): T {
      return items[Math.floor(api.next() * items.length)]!;
    },
    fork(): Rng {
      return createRng(state.seed ^ Math.floor(api.next() * 0xffffffff));
    },
  };
  return api;
}

export function serializeRng(rng: Rng): RngState {
  return { seed: rng.state.seed };
}

export function restoreRng(state: RngState): Rng {
  return createRng(state.seed);
}
